import { useCallback, useEffect, useState } from 'react';

import { useCargaPorChave } from 'hooks/useCargaPorChave';
import { getMissaoEquipe, getMissaoEquipeEcoa, getMissaoGestor, getPointsHistory } from 'services/achievement.service';
import { getKPIHistoryPag } from 'services/dashboard.service';

import { Historicos } from './pontos-por-fase';

/** Página de `find/extrato`: o "Carregar mais" da aba pede o resto com este teto. */
const ITENS_POR_LEITURA = 10000;

export interface FiltroDoCiclo {
  cycleId?: string;
}

/** Janela que a aba Reconhecimentos manda ao `find/extrato` (o servidor decide pelo `cycleId`). */
export interface JanelaDoExtrato {
  inicio?: number;
  fim?: number;
}

/** `find/extrato` inteiro, página a página, como a aba Reconhecimentos o lê ao "Carregar mais". */
async function lerExtratoInteiro(jogador: string, filtro: FiltroDoCiclo, janela: JanelaDoExtrato) {
  const itens: any[] = [];

  for (;;) {
    const { result, range } = await getKPIHistoryPag(jogador, filtro, itens.length, ITENS_POR_LEITURA, janela.inicio, janela.fim);
    const pagina = Array.isArray(result) ? result : [];
    itens.push(...pagina);
    if (pagina.length === 0 || !(range?.total > itens.length)) return itens;
  }
}

/** Os quatro históricos com os mesmos services e parâmetros das abas, para os números baterem (AJ2-30). */
async function lerHistoricos(jogador: string, filtro: FiltroDoCiclo, janela: JanelaDoExtrato): Promise<Historicos> {
  const [reconhecimentos, gestor, equipe, ecoa, engajamento] = await Promise.all([
    lerExtratoInteiro(jogador, filtro, janela),
    getMissaoGestor(jogador, filtro.cycleId),
    getMissaoEquipe(jogador, filtro.cycleId, filtro),
    getMissaoEquipeEcoa(jogador, filtro.cycleId, filtro),
    getPointsHistory(jogador, '', '', filtro),
  ]);

  return { reconhecimentos, realizacoes: [...gestor, ...equipe], ecoa, engajamento };
}

/** Uma leitura por jogador e ciclo na sessão. Falha não fica guardada, para o "Tentar de novo" ler de novo. */
const leituras = new Map<string, Promise<Historicos>>();

function carregar(chave: string, jogador: string, filtro: FiltroDoCiclo, janela: JanelaDoExtrato) {
  const guardada = leituras.get(chave);
  if (guardada) return guardada;

  const leitura = lerHistoricos(jogador, filtro, janela);
  leituras.set(chave, leitura);
  leitura.catch(() => {
    if (leituras.get(chave) === leitura) leituras.delete(chave);
  });
  return leitura;
}

/** Só para os testes: cada um começa sem leitura guardada. */
export function esquecerPontosPorFase() {
  leituras.clear();
}

/** Liga quando o elemento entra na tela e não desliga mais. Sem IntersectionObserver (navegador antigo), já liga. */
function useEntrouNaTela() {
  const [elemento, setElemento] = useState<Element | null>(null);
  const [entrou, setEntrou] = useState(false);

  useEffect(() => {
    if (!elemento || entrou) return;
    if (typeof IntersectionObserver === 'undefined') {
      setEntrou(true);
      return;
    }

    const observador = new IntersectionObserver(entradas => {
      if (entradas.some(entrada => entrada.isIntersecting)) setEntrou(true);
    });
    observador.observe(elemento);
    return () => observador.disconnect();
  }, [elemento, entrou]);

  return { entrou, observar: useCallback((no: Element | null) => setElemento(no), []) };
}

/**
 * AJ2-32: os históricos do "Pontos por fase" (e as contagens de Reconhecimentos) só são pedidos quando a seção
 * entra na tela, uma vez por jogador e ciclo na sessão. `observar` vai no `ref` da seção.
 */
export function usePontosPorFase(jogador: string | undefined, filtro: FiltroDoCiclo, janela: JanelaDoExtrato) {
  const { entrou, observar } = useEntrouNaTela();
  const chave = entrou && jogador && filtro.cycleId ? `${jogador}|${filtro.cycleId}` : undefined;
  const { carga, recarregar } = useCargaPorChave(chave, () =>
    chave && jogador ? carregar(chave, jogador, filtro, janela) : Promise.reject(new Error('sem jogador ou ciclo')),
  );

  return { carga, recarregar, observar };
}
